// dependencies
import { animated, useSpring } from "@react-spring/web";
import { GridApi, IRowNode } from "ag-grid-community";
import { format, isValid, parse, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React, { useEffect, useRef, useState } from "react";

import { faCaretDown, faCaretUp, faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Avatar, AvatarProfile, Text } from "@performyard/py-components";
import { toFixed } from "@performyard/utils-isomorphic";

import Link from "../../../../../../components/Link";
// components
import Spacer from "../../../../../../components/Spacer";
import Typography from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";

import useStore from "../../store";

import uuid from "../../../../../../utils/GenerateUUID";
import getNested from "../../../../../../utils/GetNested";
// utils
import toEmployeeUrl from "../../../../../../utils/URLs/Employee";
import { getColor } from "../../../../../../utils/getColor";
import { anonymousAvatarUrl, superRowConfig } from "./utils";

// styles
import css from "./ReportTable.module.scss";

export const personCellRenderer = (value) => {
	// determine if looking at a goal report person
	const fieldPath = value.colDef?.fieldPath || [];
	const goalPerson = fieldPath[0] === "assignee" || (fieldPath[0] === "alignment" && fieldPath[1] === "assignee");
	if (value.value && value.value.name === "Anonymous") {
		return (
			<div className={`${css.people} min-w-0 gap-1`} data-userid="person-cell-person-anonymous">
				<Avatar url={anonymousAvatarUrl} size={20} alt="Anonymous employee avatar" />
				{
					<div className={`py-line-clamp-1 ${goalPerson ? css.goalReportPerson : ""}`}>
						<Text size="paragraph-3" weight="regular">
							Redacted
						</Text>
					</div>
				}
			</div>
		);
	}
	if (value.value && value.value._id) {
		return (
			<div data-testid="person-cell-person" className={`w-full ${css.person}`}>
				<AvatarProfile
					employeeId={value.value._id}
					employeeName={value.value.name}
					avatarSize={20}
					textSize="paragraph-3"
					textWeight="regular"
					isExternal
				/>
			</div>
		);
	}

	return null;
};

export const reviewCycleCellRenderer = (value) => {
	if (value.value) {
		const cycleID = value.value.id || value.value._id;

		if (cycleID) {
			return (
				<div>
					<a href={`/review-cycle/${cycleID}`}>{value.value.name}</a>
				</div>
			);
		}
	}
	return null;
};

export const reviewReportStatusCellRenderer = (value) => {
	if (value.value) {
		const normalizedValue = value.value.toLowerCase().replace(/-/g, " ");
		switch (normalizedValue) {
			case "completed":
				return "Completed";
			case "in progress awaiting authors":
				return "Awaiting Authors";
			case "in progress awaiting signoff":
				return "Awaiting Signers";
			default:
				return null;
		}
	}
	return null;
};

export const reviewCycleStatusCellRenderer = (value) => {
	if (value.value) {
		const normalizedValue = value.value.toLowerCase().replace(/-/g, " ");
		switch (normalizedValue) {
			case "completed":
				return "Completed";
			case "scheduled":
				return "Scheduled";
			case "active":
				return "Active";
			default:
				return null;
		}
	}
	return null;
};

export const reviewFormNameCellRenderer = (value) => {
	if (value.value) {
		// Access the row data to get the review form ID
		const { id, name = "" } = value.value;

		if (id) {
			return (
				<div className="truncate">
					<Link
						isExternal
						href={`/review/${id}/`}
						className="text-blue-600 hover:text-blue-800 hover:underline"
					>
						{name}
					</Link>
				</div>
			);
		}

		return <div className="truncate">{name}</div>;
	}
	return null;
};

export const percentageCellRenderer = (value) => {
	if (value.value !== null) {
		const numValue = Number(value.value);
		const formattedValue = numValue % 1 === 0 ? numValue.toString() : toFixed(numValue, 2);
		return `${formattedValue}%`;
	}
};

export const reviewTemplateCellRenderer = (value) => {
	if (value.value && value.value.name) {
		return value.value.name;
	}
	return null;
};

export const groupCellRenderer = (value) => {
	const groupId = value?.value?._id || value?.value?.id;
	if (value?.value?.name && groupId) {
		return <div className="truncate">{value.value.name}</div>;
	}
	return null;
};

export const peopleCellRenderer = ({ value, column, node }) => {
	if (!value || !value.length) return null;

	const partialUniqueKey = `${column.colId}_${node.rowIndex}`;
	let hasRenderedAnonymous = false;
	const renderedAvatars = new Set();

	const renderPerson = (person, index) => {
		const key = `${partialUniqueKey}_${person._id}_${index}`;
		const className = `${css.peoplePerson}`;

		if (renderedAvatars.has(person._id)) {
			return null;
		}

		renderedAvatars.add(person._id);

		return (
			<div className={className} data-userid={person._id} key={key}>
				<a href={toEmployeeUrl(person._id)} target="_blank" rel="noreferrer">
					<Avatar id={person._id} size={20} name={person.name || "User"} />
				</a>
			</div>
		);
	};

	const renderAnonymousPerson = (index) => {
		const key = `${partialUniqueKey}_anonymous_${index}`;
		const className = css.anonymousPerson;
		if (column.colId !== "reviewform_authors" || !hasRenderedAnonymous) {
			hasRenderedAnonymous = true;

			return (
				<div className={`${className} min-w-0`} data-userid="anonymous" key={key}>
					<Avatar url={anonymousAvatarUrl} size={20} alt="Anonymous employee avatar" />
					{column.colId === "reviewform_authors" && (
						<div className={`${css.userName} py-line-clamp-1`}>
							<Text size="paragraph-3" weight="regular">
								Redacted
							</Text>
						</div>
					)}
				</div>
			);
		}
		return null;
	};

	return (
		<div className={css.people}>
			{value
				.flat()
				.map((person, index) =>
					person.name === "Anonymous" ? renderAnonymousPerson(index) : renderPerson(person, index)
				)}
		</div>
	);
};

export const dateCellRenderer = (value) => {
	// Check for summary function either inside the cell value object (aggregated columns)
	// or directly on the params from cellRendererParams (summarize-by date columns).
	const summaryFunctionValue =
		value.value?.currentlySelectedSummaryFunction ?? value.currentlySelectedSummaryFunction;
	if (summaryFunctionValue) {
		let dateValue = typeof value.value === "object" ? value.value.value : value.value;

		// Format the month value as a human-readable string.
		if (summaryFunctionValue === "month" && dateValue) {
			const parsedDate = parse(dateValue, "yyyy-MM", new Date());
			if (isValid(parsedDate)) {
				dateValue = format(parsedDate, "yyyy-MM");
			}
		}
		return <div className="truncate">{dateValue}</div>;
	}

	// make sure date string type so we can call parseISO
	if (value.value && typeof value.value === "string") {
		// check if the date is only a year value first
		const isYearOnly =
			/^\d{4}$/.test(value.value) && parseInt(value.value) >= 1900 && parseInt(value.value) <= 2100;
		if (isYearOnly) {
			return <div className="truncate">{value.value}</div>;
		}

		// check if it's a valid date format
		const parsedDate = parseISO(value.value);
		if (!isValid(parsedDate)) {
			return <div className="truncate">{value.value}</div>;
		}

		// Survey dates are stored with a time component (user-selected time in company timezone converted to UTC).
		// Display them in the company timezone so the date shown matches what the user set.
		// All other date fields are stored as UTC midnight and should be displayed in UTC.
		const isSurveyField = value.colDef?.fieldType === "SURVEY_FIELD";
		const displayTimezone = isSurveyField ? value.context.companyTimezone : "UTC";
		const returnDateValue = formatInTimeZone(parsedDate, displayTimezone, "MMMM dd, yyyy");
		return <div className="truncate">{returnDateValue}</div>;
	}
	return null;
};

export const booleanCellRenderer = (value) => {
	const booleanValue = value.value;

	return booleanValue === true ? "Yes" : "No";
};

export const countCellRenderer = (countType: string) => {
	const Renderer = (value) => {
		if ((value.value && typeof value.value === "string") || typeof value.value === "number") {
			return (
				<div className="truncate">{`${value.value} ${value.value === 1 ? countType : `${countType}s`}`}</div>
			);
		} else if (
			countType === "goal" &&
			value.value?._id &&
			(typeof value.value._id === "string" || typeof value.value._id === "number")
		) {
			return (
				<div className="truncate">{`${value.value._id} ${value.value._id === 1 ? countType : `${countType}s`}`}</div>
			);
		} else if (countType === "survey" && value.value?.name && typeof value.value?.name === "number") {
			return (
				<div className="truncate">{`${value.value.name} ${value.value.name === 1 ? countType : `${countType}s`}`}</div>
			);
		}
		return null;
	};
	Renderer.displayName = `CountCellRenderer(${countType})`;
	return Renderer;
};

export const reviewReportCountCellRenderer = countCellRenderer("form");
export const employeeReportCountCellRenderer = countCellRenderer("employee");
export const goalReportCountCellRenderer = countCellRenderer("goal");
export const surveyReportCountCellRenderer = countCellRenderer("survey");

const cellRendererMap = {
	reviewReport: reviewReportCountCellRenderer,
	employeeReport: employeeReportCountCellRenderer,
	goalReport: goalReportCountCellRenderer,
	surveyReport: surveyReportCountCellRenderer,
};

export const determineCellRenderer = (reportType: string) => {
	return cellRendererMap[reportType] || null;
};

export const numericCellRenderer = (value) => {
	if ((value.value || value.value === 0) && typeof value.value === "number") {
		const roundedValue = toFixed(value.value, value.decimalPlaces);
		return <div className="truncate">{roundedValue}</div>;
	}
	return null;
};

/**
 * Goal cell renderers
 */
export const goalCategoriesCellRenderer = (value) => {
	if (value.value && Array.isArray(value.value)) {
		return <div className="truncate">{value.value.map((cat) => cat.name).join(", ")}</div>;
	}
	return null;
};

export const goalOutlookCellRenderer = (value) => {
	if (value.value) {
		return (
			<div className="relative w-full">
				<div className="absolute flex w-full items-center gap-x-1">
					<div
						data-testid="outlook-color-dot"
						className="h-3 w-3 shrink-0 rounded-full"
						style={{ backgroundColor: value.value.color }}
					></div>
					<div className="truncate">{value.value.name}</div>
				</div>
			</div>
		);
	}
	return null;
};

export const currentGoalProgressCellRenderer = (value) => {
	if (value.value || value.value === 0) {
		const percentage = toFixed(value.value, 0);
		return <div className="truncate">{percentage}%</div>;
	}
	return null;
};

export const goalTargetCellRenderer = (value) => {
	const beforeUnits = ["$", "£", "€", "¥"];
	const units = value.data?.units;
	if ((value.value || value.value === 0) && units) {
		if (beforeUnits.includes(units)) {
			return <div className="truncate">{`${units}${value.value}`}</div>;
		}
		return <div className="truncate">{`${value.value}${units.length > 1 ? " " : ""}${units}`}</div>;
	}
	return null;
};

export const goalStatusCellRenderer = (value) => {
	const statusMap = { active: "Active", archived: "Archived", done: "Completed", deleted: "Deleted" };
	const valueOrSummaryFieldValue = value.value?._id || value.value;
	if (valueOrSummaryFieldValue) {
		return <div className="truncate">{statusMap[valueOrSummaryFieldValue] || valueOrSummaryFieldValue}</div>;
	}
	return null;
};

export const goalTransparencyCellRenderer = (value) => {
	const transparencyMap = { private: "Private", public: "Public" };
	if (value.value) {
		return <div className="truncate">{transparencyMap[value.value] || value.value}</div>;
	}
	return null;
};

export const goalNameCellRenderer = (value) => {
	const goalId = value.data?._id;
	if (value.value) {
		return (
			<div className="relative w-full">
				<Link isExternal href={`/goal/${goalId}/`} className="absolute w-full">
					<div className="truncate">{value.value}</div>
				</Link>
			</div>
		);
	}
	return null;
};

export const goalCellRenderer = (value) => {
	const statusMap = { active: "Active", archived: "Archived", done: "Completed", deleted: "Deleted" };
	if (value.value) {
		const alignmentColor = value.value.outlook?.color || getColor("primary", 5);
		const alignmentStatus = statusMap[value.value.status] || value.value.status;
		const alignmentId = value.value._id;
		return (
			<div className="relative w-full">
				<Link isExternal href={`/goal/${alignmentId}/`} className="absolute flex w-full items-center gap-x-1">
					<div
						data-testid="outlook-color-dot"
						className="h-3 w-3 shrink-0 rounded-full"
						style={{ backgroundColor: alignmentColor }}
					></div>
					<div className="truncate">
						<span>({alignmentStatus}) </span>
						<span>{value.value.name}</span>
					</div>
				</Link>
			</div>
		);
	}
	return null;
};

// renders survey name as a link to the survey details page
export const surveyNameCellRenderer = (value) => {
	if (value.value) {
		// Access the row data to get the survey ID
		const { id, name = "" } = value.value;

		if (id) {
			return (
				<div className="truncate">
					<Link isExternal href={`/surveys/${id}`}>
						{name}
					</Link>
				</div>
			);
		}

		return <div className="truncate">{name}</div>;
	}
	return null;
};

interface SuperRowCellProps {
	colDef: any;
	value: string[];
	node: any;
	column: any;
	api: GridApi;
}

export const superRowCellRenderer = (props: SuperRowCellProps) => {
	const [addedRow, setAddedRow] = useState<IRowNode | undefined>(undefined);
	const superRowToggleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [
		getExpandedSuperRowCell,
		setExpandedSuperRowCell,
		getExpandingSuperRowCell,
		setExpandingSuperRowCell,
		isSummarizeRowsView,
	] = useStore((state) => [
		state.getExpandedSuperRowCell,
		state.setExpandedSuperRowCell,
		state.getExpandingSuperRowCell,
		state.setExpandingSuperRowCell,
		state.isSummarizeRowsView,
	]);

	const isSummary = isSummarizeRowsView || props.colDef.field.startsWith("groupedquestion_");

	const newRowData = {
		superRow: true, // this is the flag to indicate that this is a super row
		question: props.colDef.headerTooltip,
		colId: props.colDef.field,
		answers: props.value,
		isSummary, // need to know this to calculate row height properly
	};

	const expandedSuperRowCell = getExpandedSuperRowCell();
	const isOpen = () =>
		expandedSuperRowCell?.row === props.node.rowIndex && expandedSuperRowCell?.col === props.column.instanceId;
	const nextRowIndex = props.node.rowIndex + 1;
	const superRowHeight = superRowConfig.getRowHeight({ data: newRowData }) || 0;

	const queryDomElements = () => {
		const rowElement = document.querySelector(`[row-index="${props.node.rowIndex}"]`);
		// we need to dynamically alter any row-spanning cells ovelapping the row that was clicked
		// these have the class .cell-span
		const cellSpanElement = rowElement?.querySelector<HTMLDivElement>(".cell-span") || null;
		const cellSpanOriginalHeight = cellSpanElement?.clientHeight || 0;

		const nextRowElement = document.querySelector(`[row-index="${nextRowIndex}"]`);
		// get any cell element with the class cell-span
		const nextCellSpanElement = nextRowElement?.querySelector<HTMLDivElement>(".cell-span") || null;
		let firstSpannedCell: HTMLDivElement | null = null;
		let ii: number = props.node.rowIndex - 1;
		if (cellSpanElement) {
			// this rewinds through any previous rows to find the first spanned cell
			// which needs to have it's bottom border removed when there is a super row crossing the cell-span
			for (ii; ii >= 0; ii--) {
				const previousRowElement = document.querySelector(`[row-index="${ii}"]`);
				if (!previousRowElement) {
					break;
				}
				firstSpannedCell = previousRowElement.querySelector<HTMLDivElement>(".first-cell-span");
				if (firstSpannedCell) {
					break;
				}
			}
		}
		return { firstSpannedCell, nextCellSpanElement, cellSpanElement, cellSpanOriginalHeight };
	};

	useEffect(() => {
		const borderPixels = 2;
		const { firstSpannedCell, nextCellSpanElement, cellSpanElement, cellSpanOriginalHeight } = queryDomElements();

		if (
			expandedSuperRowCell &&
			expandedSuperRowCell.row === props.node.rowIndex &&
			expandedSuperRowCell.col === props.column.instanceId &&
			!addedRow
		) {
			// remove any existing super rows, only happens on super slow CPUs when clicking between toggle cells too fast,
			// this is a failsafe to clean up after because another super row should not be open at this point
			const existingSuperRows: IRowNode["data"][] = props.api
				.getRenderedNodes()
				.filter((node) => node.data.superRow)
				.map((node) => node.data);
			if (existingSuperRows.length) {
				// if there are any existing super rows, remove them
				props.api.applyTransaction({ remove: existingSuperRows });
			}
			const newRow = props.api.applyTransaction({ add: [newRowData], addIndex: nextRowIndex });
			// set this as the focused cell
			props.api.setFocusedCell(props.node.rowIndex, props.column, null);
			setAddedRow(newRow?.add[0]);
			if (cellSpanElement && nextCellSpanElement && !cellSpanElement.classList.contains("expanded")) {
				// add the height of the super row to the cell if not already added
				cellSpanElement.classList.add("expanded");
				cellSpanElement.style.height = `${cellSpanOriginalHeight + superRowHeight + borderPixels}px`;
			}
			if (firstSpannedCell && !firstSpannedCell.classList.contains("expanded")) {
				// Get the original height of the first spanned cell (before any super row expansion)
				// Store the original height in a data attribute if not already stored
				let firstSpannedCellOriginalHeight = firstSpannedCell.dataset.originalHeight;
				if (!firstSpannedCellOriginalHeight) {
					firstSpannedCellOriginalHeight = firstSpannedCell.clientHeight.toString();
					firstSpannedCell.dataset.originalHeight = firstSpannedCellOriginalHeight;
				}
				// Add the super row height to the first spanned cell as well
				firstSpannedCell.classList.add("expanded");
				firstSpannedCell.style.height = `${parseInt(firstSpannedCellOriginalHeight) + superRowHeight + borderPixels}px`;
			}
		} else {
			if (addedRow?.data) {
				const rowExists = addedRow.id ? props.api.getRowNode(addedRow.id) : null;
				if (rowExists) {
					props.api.applyTransaction({ remove: [addedRow.data] });
				}
				if (cellSpanElement && cellSpanElement.classList.contains("expanded")) {
					// restore the height of the cell span element
					cellSpanElement.style.height = `${cellSpanOriginalHeight - superRowHeight + borderPixels}px`;
					cellSpanElement.classList.remove("expanded");
				}
				if (firstSpannedCell && firstSpannedCell.classList.contains("expanded")) {
					// Get the original height of the first spanned cell from the data attribute
					const firstSpannedCellOriginalHeight = firstSpannedCell.dataset.originalHeight;
					if (firstSpannedCellOriginalHeight) {
						// Restore the original height of the first spanned cell
						firstSpannedCell.style.height = `${parseInt(firstSpannedCellOriginalHeight) + borderPixels}px`;
					}
					firstSpannedCell.classList.remove("expanded");
					firstSpannedCell.classList.remove("no-bottom-border"); // restore the border from the first spanned cell
				}
			}
			setAddedRow(undefined);
		}
	}, [expandedSuperRowCell]);

	useEffect(() => {
		return () => {
			if (superRowToggleTimeoutRef.current) {
				clearTimeout(superRowToggleTimeoutRef.current);
				setExpandingSuperRowCell(null);
			}
		};
	}, []);

	const handleOpen = () => {
		const openingCell = { row: props.node.rowIndex, col: props.column.instanceId };

		// if a row is already expanding, prevent this row from expanding
		if (getExpandingSuperRowCell()) {
			return;
		}

		setExpandedSuperRowCell(null);
		// we have to ensure that any open super row is closed before accessing the rowIndex, because it may have changed
		// this timeout is long enugh for another cell rednderer's useEffect to run and apply classes, etc.
		setExpandingSuperRowCell(openingCell);

		superRowToggleTimeoutRef.current = setTimeout(() => {
			const currentCell = { row: props.node.rowIndex, col: props.column.instanceId };
			if (
				getExpandingSuperRowCell()?.col === openingCell.col &&
				getExpandingSuperRowCell()?.row === openingCell.row &&
				!getExpandedSuperRowCell()
			) {
				setExpandingSuperRowCell(null);
				setExpandedSuperRowCell(currentCell);
			}
		}, 100);
	};

	const numberOfDistinctAnswers = new Set(props.value).size;
	const numberOfAnswers = props.value?.length;
	const displayedCount = isSummary ? numberOfDistinctAnswers : numberOfAnswers;
	const answerWording = isSummary ? "Distinct Answer" : "Answer";

	const displayedText = displayedCount === 1 ? `1 ${answerWording}` : `${displayedCount} ${answerWording}s`;

	return (
		<>
			{props.value?.length ? (
				<div className={css.superRowToggleCell}>
					<span
						className={css.superRowToggleCellText}
						style={{ color: isOpen() ? getColor("primary", 5) : undefined }}
						onClick={() => {
							isOpen()
								? (() => {
										setExpandedSuperRowCell(null);
										setExpandingSuperRowCell(null);
									})()
								: handleOpen();
						}}
					>
						<Typography size="paragraph-3" weight="regular">
							{displayedText}
						</Typography>
						{isOpen() ? (
							<FontAwesomeIcon
								className={css.expandedCell}
								data-testid="super-row-open-icon"
								icon={faCaretUp}
							/>
						) : (
							<FontAwesomeIcon data-testid="super-row-closed-icon" icon={faCaretDown} />
						)}
					</span>
				</div>
			) : null}
		</>
	);
};

interface SuperRowProps {
	data: { question: string; answers: string[]; colId: string; isSummary: boolean };
	api: GridApi;
}

export const superRow = (props: SuperRowProps) => {
	const { data } = props;
	const superRowRef = useRef<HTMLDivElement>(null);
	const [currentQuestionWidth, setCurrentQuestionWidth] = useState(Infinity);
	const [currentAnswerWidth, setCurrentAnswerWidth] = useState(Infinity);

	const columns = props.api.getColumns();

	const [getEnabledGridColumns, getSummaryColumnMetadata] = useStore((state) => [
		state.getEnabledGridColumns,
		state.getSummaryColumnMetadata,
	]);

	const distinctAnswers = new Set(data.answers);
	const springs = useSpring(
		superRowConfig.getRowAnimationConfig(data.isSummary ? distinctAnswers.size : data.answers.length)
	);

	useEffect(() => {
		// we need to keep track of the true width of the super row, to ensure that the question and answer text is truncated correctly
		const resizeObserver = new ResizeObserver((entries) => {
			const { width: fullWidth } = entries[0].contentRect;
			if (fullWidth > 0) {
				const { questionWidth, answerWidth } = superRowConfig.getWidths(fullWidth + 34);
				setCurrentQuestionWidth(questionWidth);
				setCurrentAnswerWidth(answerWidth);
			}
		});

		if (superRowRef.current) {
			resizeObserver.observe(superRowRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const columnVisibilityMap = getSummaryColumnMetadata();
	const visibleColumnIds = getEnabledGridColumns()
		.filter((c) => getNested(columnVisibilityMap, [c.field, "isColumnVisible"], false))
		.map((c) => c.field);

	// this gets the widths of the columns, in case they do not span across the visible report area
	const columnWidths =
		columns?.reduce(
			(acc, curr) =>
				curr.getColId().startsWith("summary_") || visibleColumnIds.includes(curr.getColId())
					? curr.getActualWidth() + acc
					: acc,
			0
		) || 0;

	const { superRowWidth, questionWidth, answerWidth } = superRowConfig.getWidths(columnWidths);
	const sortedAnswers = [...data.answers].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

	return (
		<animated.div
			ref={superRowRef}
			className={css.superRow}
			// css min() works here to determine the width of the super row itself
			style={{ ...springs, ...{ width: `min(calc(100% - 32px), ${superRowWidth}px)` } }}
		>
			<span className={css.superRowQuestion}>
				<TruncatedText
					text={data.question}
					// we use Math.min for this because TruncatedText requires a number
					maxWidth={Math.min(questionWidth, currentQuestionWidth)}
					size="paragraph-3"
					weight="semi-bold"
				/>
				<Typography weight="semi-bold" size="paragraph-3">
					:
				</Typography>
			</span>
			<Spacer height={2} />
			<div className={css.superRowAnswers}>
				{data.isSummary ? (
					<SummarizedSuperRow
						answers={sortedAnswers}
						answerWidth={Math.min(answerWidth, currentAnswerWidth)}
					/>
				) : (
					<>
						{sortedAnswers.map((answer: string) => {
							const uniqueId = uuid();
							return (
								<div
									data-testid="detail-super-row-answer"
									key={`${answer}${uniqueId}`}
									className={css.superRowAnswer}
								>
									<FontAwesomeIcon icon={faCircleCheck} color={getColor("primary", 5)} />
									<div className={css.answerBar}>
										<TruncatedText
											text={answer}
											size="paragraph-3"
											weight="regular" // we use Math.min for this because TruncatedText requires a number
											maxWidth={Math.min(answerWidth, currentAnswerWidth)}
										/>
									</div>
								</div>
							);
						})}
					</>
				)}
			</div>
		</animated.div>
	);
};

interface SummarizedSuperRowProps {
	answers: string[];
	answerWidth: number;
}

const SummarizedSuperRow = ({ answers, answerWidth }: SummarizedSuperRowProps) => {
	// reduce to map of answer counts
	const dataCountMap = answers.reduce((acc: { [key: string]: number }, curr) => {
		acc[curr] = (acc[curr] || 0) + 1;
		return acc;
	}, {});

	const dataDisplayMap = Object.entries(dataCountMap).reduce((acc: { [key: string]: string }, [key, value]) => {
		acc[key] = `${value} (${toFixed((value / answers.length) * 100, 0)}%)`;
		return acc;
	}, {});

	const widestDataDisplayWidth = superRowConfig.getWidestStringWidth(Object.values(dataDisplayMap));
	const fullWidth = answerWidth - widestDataDisplayWidth + 30; // 8 is the gap between text and the answer bar, but I have no idea where this 22px comes from either

	// sort by count
	const sortedAnswers = Object.keys(dataCountMap).sort((a, b) => dataCountMap[b] - dataCountMap[a]);

	return (
		<>
			{sortedAnswers.map((answer: string) => {
				// animate from evenly distributed width to the actual width for the percentage
				const startWidth = superRowConfig.numberToWidthInPixels(1, Object.keys(dataCountMap).length, fullWidth);
				const width = superRowConfig.numberToWidthInPixels(dataCountMap[answer], answers.length, fullWidth);
				const isFullWidth = width === fullWidth;
				// animate the percentage width into the bar
				const springs = useSpring(superRowConfig.getAnswerBarAnimationConfig(width, startWidth));
				return (
					<div data-testid="summarized-super-row-answer" key={answer} className={css.superRowAnswer}>
						<span style={{ minWidth: `${widestDataDisplayWidth}px` }}>
							<Typography size="paragraph-3" weight="semi-bold">
								{dataDisplayMap[answer]}
							</Typography>
						</span>
						<div className={css.answerBar}>
							<animated.div
								className={`${css.answerPercentage} ${isFullWidth ? css.fullWidth : ""}`}
								style={springs}
							/>
							<TruncatedText text={answer} size="paragraph-3" weight="regular" maxWidth={answerWidth} />
						</div>
					</div>
				);
			})}
		</>
	);
};
